import React, { useEffect, useState } from 'react';
import { billingApi, formatApiError } from '../../services/api';
import { isNativeApp, purchasePlanNative, restorePurchasesNative } from '../../lib/nativeBridge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { CheckCircle2, Loader2, Smartphone, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const APP_STORE_URL = 'https://apps.apple.com/app/id6775250788';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.ikonetsolutions.ithelpdesk';

const PLANS = [
  {
    key: 'standard',
    name: 'Standard',
    monthlyId: 'standard_monthly',
    annualId: 'standard_annual',
    monthlyPrice: 49,
    annualPrice: 490, // 2 mesi gratis rispetto a 12x49
    features: ['Fino a 10 tecnici', 'WhatsApp + Telegram', 'Asset Management', 'Knowledge Base + SLA'],
  },
  {
    key: 'premium',
    name: 'Premium',
    monthlyId: 'premium_monthly',
    annualId: 'premium_annual',
    monthlyPrice: 99,
    annualPrice: 990,
    features: ['Tecnici illimitati', 'Tutto lo Standard', 'API personalizzate', 'Account manager dedicato'],
    highlight: true,
  },
];

export default function UpgradePage() {
  const [license, setLicense] = useState(null);
  const [annual, setAnnual] = useState(false);
  const [purchasingPlan, setPurchasingPlan] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const native = isNativeApp();

  useEffect(() => {
    billingApi.getLicense().then((res) => setLicense(res.data)).catch(() => {});
  }, []);

  const handlePurchase = async (plan) => {
    const planId = annual ? plan.annualId : plan.monthlyId;
    setPurchasingPlan(planId);
    try {
      const result = await purchasePlanNative(planId);
      if (result.success) {
        await billingApi.validateIAP({
          product_id: result.planId,
          transaction_id: result.transactionId,
          platform: result.platform,
          receipt_data: result.receiptData,
          purchase_token: result.purchaseToken,
        });
        toast.success(`Piano ${plan.name} attivato`);
        const res = await billingApi.getLicense();
        setLicense(res.data);
      } else if (!result.cancelled) {
        toast.error(result.error || "Acquisto non riuscito. Riprova.");
      }
    } catch (err) {
      toast.error(formatApiError(err) || 'Acquisto non riuscito. Riprova.');
    } finally {
      setPurchasingPlan(null);
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    try {
      const result = await restorePurchasesNative();
      if (result.purchases.length === 0) {
        toast.info('Nessun acquisto da ripristinare');
        return;
      }
      for (const p of result.purchases) {
        try {
          await billingApi.validateIAP({
            product_id: p.planId,
            transaction_id: p.transactionId,
            platform: p.platform,
            receipt_data: p.receiptData,
            purchase_token: p.purchaseToken,
          });
        } catch (_) {
          // Best-effort: keep applying the other restored purchases even if
          // one fails validation.
        }
      }
      toast.success('Acquisti ripristinati');
      const res = await billingApi.getLicense();
      setLicense(res.data);
    } catch (err) {
      toast.error('Impossibile ripristinare gli acquisti.');
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="max-w-3xl">
      {license?.license_type === 'trial' && license.days_remaining != null && (
        <div className="mb-6 p-4 rounded-[12px] text-sm font-medium" style={{ background: '#FBEEDA', color: '#9A5B0A', border: '1px solid #F2D9B0' }}>
          Il tuo trial scade tra {license.days_remaining} {license.days_remaining === 1 ? 'giorno' : 'giorni'}. Scegli un piano per continuare senza interruzioni.
        </div>
      )}

      <div className="flex items-center justify-center gap-3 mb-8">
        <span className={`text-sm font-semibold ${!annual ? 'text-[#0E1726]' : 'text-[#8593AB]'}`}>Mensile</span>
        <button
          onClick={() => setAnnual(!annual)}
          className="relative w-12 h-7 rounded-full transition-colors"
          style={{ background: annual ? '#2A52C4' : '#D8DEE9' }}
          data-testid="billing-cycle-toggle"
        >
          <span className="absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform" style={{ transform: annual ? 'translateX(20px)' : 'translateX(0)' }} />
        </button>
        <span className={`text-sm font-semibold ${annual ? 'text-[#0E1726]' : 'text-[#8593AB]'}`}>
          Annuale <span className="text-[#16A34A]">(2 mesi gratis)</span>
        </span>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        {PLANS.map((plan) => {
          const planId = annual ? plan.annualId : plan.monthlyId;
          const price = annual ? plan.annualPrice : plan.monthlyPrice;
          const isCurrent = license?.license_type === plan.key;
          const isPurchasing = purchasingPlan === planId;
          return (
            <Card key={plan.key} className={`border shadow-none ${plan.highlight ? 'border-[#2A52C4]' : 'border-[#E4E4E7]'}`}>
              <CardContent className="p-6">
                <div className="text-lg font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{plan.name}</div>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-3xl font-bold">€{price}</span>
                  <span className="text-[#71717A] text-sm">/{annual ? 'anno' : 'mese'}</span>
                </div>
                <ul className="space-y-2 mb-6">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-[#3F3F46]">
                      <CheckCircle2 className="w-4 h-4 text-[#16A34A] shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>

                {isCurrent ? (
                  <div className="text-center text-sm font-semibold text-[#16A34A] py-2.5">Piano attivo</div>
                ) : native ? (
                  <Button
                    onClick={() => handlePurchase(plan)}
                    disabled={isPurchasing}
                    className={`w-full font-medium ${plan.highlight ? 'bg-[#002FA7] hover:bg-[#002FA7]/90 text-white' : 'bg-white border border-[#E4E4E7] text-[#0E1726] hover:bg-gray-50'}`}
                    data-testid={`upgrade-${plan.key}-btn`}
                  >
                    {isPurchasing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    {isPurchasing ? 'Acquisto in corso...' : `Attiva ${plan.name}`}
                  </Button>
                ) : (
                  <div className="text-xs text-[#71717A] text-center py-1.5">
                    Disponibile solo dall'app
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {native ? (
        <div className="mt-6 text-center">
          <button onClick={handleRestore} disabled={restoring}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#71717A] hover:text-[#0E1726]"
            data-testid="restore-purchases-btn">
            {restoring ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
            Ripristina acquisti
          </button>
        </div>
      ) : (
        <div className="mt-8 p-5 rounded-[14px] flex items-center gap-4" style={{ background: '#F2F4F8', border: '1px solid #E4E8EF' }}>
          <Smartphone className="w-9 h-9 text-[#2A52C4] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-[#0E1726]">L'abbonamento si attiva dall'app</div>
            <div className="text-xs text-[#71717A] mt-0.5">Scarica IT Helpdesk per iOS o Android per scegliere il piano e pagare in sicurezza tramite App Store o Google Play.</div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-[#2A52C4] hover:underline">App Store</a>
            <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-[#2A52C4] hover:underline">Google Play</a>
          </div>
        </div>
      )}
    </div>
  );
}
